/**
 * Не хватает денег на платёж: арифметика, а не тревога.
 *
 * Экран отвечает на вопрос «сколько мне не хватает» двумя разными числами,
 * и смешивать их нельзя:
 *
 * · **сейчас** — платёж минус уже отложенное. Это деньги, которых нет на
 *   счетах сегодня;
 * · **после дохода** — то же минус приход, который ожидается до срока. Это
 *   прогноз, и он верен ровно настолько, насколько верен приход.
 *
 * Поэтому и заголовок условный: «Если доход придёт 25 сентября, не хватит
 * 2 000 ₽». Безусловное «не хватает 2 000 ₽» означало бы, что приложение
 * уже посчитало чужие деньги своими, — а пока их нет на счёте, не хватает
 * двенадцати тысяч, и человек имеет право это знать.
 *
 * Ничего не применяется само: список действий — это переходы, а не правки.
 */
import type { ReactNode } from 'react';
import {
  ZERO, clampPos, formatMoney, sub,
  type Coverage, type Money,
} from '@skoka/core';
import { SheetWrap } from './sheet.js';
import { Tip } from './tip.js';
import { dayMonthYear } from './text.js';

export function ShortfallSheet({ coverage, onClose, onRemedy, onPlanIncome, onSettle, onCard, planned }: {
  coverage: Coverage;
  /** Назначенное из приходов на этот платёж — с «Отменить» (04.11.2026). */
  planned?: ReactNode;
  onClose: () => void;
  /** Варианты: что можно изменить, чтобы деньги нашлись. */
  onRemedy: (gap: Money) => void;
  /** Назначить будущий приход на этот платёж. */
  onPlanIncome: () => void;
  /** Платёж уже сделан — записать его. */
  onSettle: () => void;
  /** Кредитка: «Уже внесено» — в её настройке вместо «Платёж уже сделан». */
  onCard?: () => void;
}) {
  const now = clampPos(sub(coverage.amount, coverage.secured));
  const expected = coverage.plannedInTime;
  const after = coverage.gap;
  const day = coverage.expectedBy ? dayMonthYear(coverage.expectedBy) : null;

  return (
    <SheetWrap
      title={expected > ZERO && day
        ? `Если доход придёт ${day}, не хватит ${formatMoney(after)}`
        : `Не хватает ${formatMoney(now)}`}
      onClose={onClose}>
      <div className="note" style={{ marginTop: 0 }}>
        {coverage.label} · оплатить до {dayMonthYear(coverage.dueDate)}
      </div>

      <section className="blk">
        <div className="row"><div className="rl">Платёж</div>
          <div className="rv num">{formatMoney(coverage.amount)}</div></div>
        <div className="row"><div className="rl">Уже отложено
          <span className="rn">{coverage.instanceId.startsWith('card:') ? 'внесено после выписки и в конвертах' : 'деньги закреплены конвертом'}</span></div>
          <div className="rv num">{formatMoney(coverage.secured)}</div></div>
        <div className="row"><div className="rl">Не хватает сейчас
          <span className="rn">из денег, которые есть на счетах</span></div>
          <div className="rv num">{formatMoney(now)}</div></div>
        {expected > ZERO && (
          <>
            <div className="row"><div className="rl">Ожидаемый доход
              <Tip>Ожидаемый доход не входит в доступные деньги: пока он не поступил,
                не хватает {formatMoney(now)}. Если придёт {day} до оплаты,
                останется найти {formatMoney(after)}.</Tip>
              <span className="rn">{day ? `назначен на ${day}` : 'дата не названа'}</span></div>
              <div className="rv num">{formatMoney(expected)}</div></div>
            <div className="row lvl-subtotal"><div className="rl">
              Останется найти, если доход придёт вовремя</div>
              <div className="rv num">{formatMoney(after)}</div></div>
          </>
        )}
      </section>

      {planned}

      {coverage.state === 'late' && (
        <div className="note warn">
          Назначенные деньги приходят позже срока — к {dayMonthYear(coverage.dueDate)}
          {' '}их не будет.
        </div>
      )}

      <section className="blk">
        <div className="blkhead"><b>Что можно сделать
          <Tip>Уменьшить свободные траты, перенести часть целей, использовать
            подушку — всё это отдельные решения, и ни одно не применится само.</Tip></b></div>
        <button className="btn" onClick={() => onRemedy(after > ZERO ? after : now)}>
          Посмотреть варианты
        </button>
        <button className="btn" onClick={onPlanIncome}>
          Назначить будущий приход
        </button>
        {onCard
          ? <button className="btn" onClick={onCard}>Уже внесено на карту</button>
          : <button className="btn" onClick={onSettle}>Платёж уже сделан</button>}
      </section>
    </SheetWrap>
  );
}
